// ============================================================================
// 🔒 LOCKED CODE - ENHANCED VERSION - DO NOT MODIFY WITHOUT AUTHORIZATION
// Lock Date: December 8, 2025
// Documentation: /app/CODE_LOCKED_DO_NOT_MODIFY.md
//
// THIS IS THE PRIMARY TOOL FOR SEMESTER/INSTALLMENT TRACKING
//
// Enhanced Features (Option 1 Implementation):
// 1. Dashboard Statistics (Total Students, Total Due, Overdue, Collection Rate)
// 2. Advanced Filters (Course, Session, Overdue Status)
// 3. Bulk Actions (Send Reminders to multiple students)
// 4. Student Selection (Checkboxes for bulk operations)
//
// CRITICAL: This report shows installment-wise breakdown
// DO NOT remove dashboard, filters, or bulk actions
// ============================================================================

/**
 * Fee Installment Report Component - ENHANCED
 * 
 * Purpose: Display and manage fee installment data for students
 * Features: Dashboard, Filters, Bulk Actions, Installment Details
 * 
 * IMPORTANT: BALANCE FEE CALCULATION FORMULA - DO NOT MODIFY
 * ============================================================
 * Balance Fee = Course Fee - Admission - Booking - Discount - Scholarship - Installments Paid
 * 
 * This formula is FIXED and must not be changed without explicit approval.
 * All amounts (Admission, Booking, etc.) are ALREADY PAID, so they are SUBTRACTED.
 * Must match backend calculation in /app/backend/routers/students.py
 */

import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { Search, Eye, Edit, Calendar, DollarSign, User, Download, Filter, Printer, X, Bell, CheckSquare, TrendingUp, AlertTriangle, Clock, Trash2 } from 'lucide-react';
import api from '@/api/api';
import { generateInstallmentSettingPDF } from '@/utils/installmentSettingPrintGenerator';
import { formatDate } from '../../utils/dateTimeUtils';

const FeeInstallmentReport = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [recentPayments, setRecentPayments] = useState([]);
  const [showPayments, setShowPayments] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showDashboard, setShowDashboard] = useState(true);
  
  // Only Admin / SuperAdmin can delete payments (backend enforced too)
  const canDeletePayment = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const r = u.role || '';
      return r === 'Admin' || r === 'SuperAdmin';
    } catch { return false; }
  })();
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Filter states
  const [filters, setFilters] = useState({
    course: '',
    session: '',
    overdueOnly: false,
    paidOnly: false,
    unpaidOnly: false
  });

  // Master data
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    fetchStudentsWithInstallments();
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [coursesRes, sessionsRes] = await Promise.all([
        api.get('/master-settings/courses'),
        api.get('/master-settings/sessions')
      ]);
      setCourses(coursesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchStudentsWithInstallments = async () => {
    setLoading(true);
    try {
      // Single optimized API call instead of N+1 calls
      const response = await api.get('/installment-payments/students-installment-summary');
      const studentsData = Array.isArray(response.data) ? response.data : [];
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate dashboard statistics
  const calculateDashboardStats = () => {
    const today = new Date();
    let totalStudents = filteredStudents.length;
    let totalDue = 0;
    let totalOverdue = 0;
    let totalCollected = 0;
    let overdueCount = 0;

    filteredStudents.forEach(student => {
      const balance = student.calculated_balance || 0;
      const installmentsPaid = student.installments_paid || 0;
      
      totalDue += balance;
      totalCollected += installmentsPaid;
      
      // Check if student has overdue installments
      if (student.has_overdue) {
        overdueCount++;
        totalOverdue += student.overdue_amount || 0;
      }
    });

    return {
      totalStudents,
      totalDue: Math.round(totalDue),
      totalOverdue: Math.round(totalOverdue),
      totalCollected: Math.round(totalCollected),
      overdueCount,
      collectionRate: totalCollected > 0 ? Math.round((totalCollected / (totalCollected + totalDue)) * 100) : 0
    };
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      course: '',
      session: '',
      overdueOnly: false,
      paidOnly: false,
      unpaidOnly: false
    });
  };

  // Handle bulk student selection
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleAllStudents = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  // Send reminders to selected students
  const sendReminders = async () => {
    if (selectedStudents.length === 0) {
      alert('Please select students to send reminders');
      return;
    }

    try {
      await api.post('/fees/send-reminders', {
        student_ids: selectedStudents
      });
      alert(`Reminders sent to ${selectedStudents.length} student(s)`);
      setSelectedStudents([]);
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert('Failed to send reminders');
    }
  };


  const viewInstallments = async (student) => {
    try {
      const response = await api.get(`/fees/installments/${student.id}`);
      const installmentsData = response.data;
      
      // Fetch payment records for this student to get payment modes
      const paymentsResponse = await api.get(`/fees/payments?student_id=${student.id}`);
      const payments = paymentsResponse.data;
      
      // Map payment modes to installments based on paid_amount and dates
      const installmentsWithPaymentMode = installmentsData.map(inst => {
        if (inst.status === 'Paid' && inst.paid_amount > 0) {
          // Try to find matching payment
          const matchingPayment = payments.find(p => 
            Math.abs(p.amount - inst.paid_amount) < 1 // Match amounts (with small tolerance)
          );
          
          if (matchingPayment) {
            return {
              ...inst,
              payment_mode: matchingPayment.payment_mode || 'Cash',
              paid_date: matchingPayment.payment_date
            };
          }
        }
        return inst;
      });
      
      setInstallments(installmentsWithPaymentMode);
      setSelectedStudent(student);
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching installments:', error);
      alert('No installments found for this student');
      setInstallments([]);
    }
  };

  // Fetch recent payments for a student
  const fetchRecentPayments = async (studentId) => {
    try {
      const response = await api.get(`/installment-payments/student/${studentId}/recent-payments`);
      setRecentPayments(response.data.payments || []);
      setShowPayments(true);
    } catch (error) {
      console.error('Error fetching payments:', error);
      alert('Error fetching payment history');
    }
  };

  // Delete a payment
  const deletePayment = async (paymentId) => {
    const confirmMsg =
      'Delete this Payment?\n\n' +
      'THIS WILL ALSO REMOVE THE ENTRY FROM:\n' +
      '• Cash Book (if paid by Cash)\n' +
      '• Bank Book + Bank balance will be reversed (if non-Cash)\n' +
      '• Income Reports\n' +
      '• Student outstanding will be recalculated\n\n' +
      'This action will be recorded in the Delete Logs (Audit). Cannot be undone.';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setDeletingPayment(paymentId);
    try {
      const response = await api.delete(`/installment-payments/payment/${paymentId}`);
      const c = response.data?.cascade || {};
      alert(
        `Payment deleted successfully!\n\n` +
        `Amount reversed: ₹${Number(response.data?.amount_reversed || 0).toLocaleString('en-IN')}\n\n` +
        `Cascade cleanup:\n` +
        `• Incomes removed: ${c.incomes_deleted || 0}\n` +
        `• Cash Book entries removed: ${c.cash_register_deleted || 0}\n` +
        `• Bank Book entries removed: ${c.bank_transactions_deleted || 0}\n` +
        `• Bank balance reversed: ${c.banks_balance_reversed || 0}\n` +
        `• Fee installments reset: ${c.fee_installments_reset || 0}\n` +
        `• Student outstanding updated: ${c.student_balance_updated ? 'Yes' : 'No'}`
      );
      
      // Refresh data
      setShowPayments(false);
      setRecentPayments([]);
      if (selectedStudent) {
        viewInstallments(selectedStudent);
      }
      fetchStudentsWithInstallments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Error deleting payment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingPayment(null);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedStudent(null);
    setInstallments([]);
    setShowPayments(false);
    setRecentPayments([]);
  };

  // Repair: recompute installments + balance based on actual surviving fee_payments
  const recomputeInstallments = async (student) => {
    if (!window.confirm(
      `Recompute installments for ${student.full_name}?\n\n` +
      `This will scan all surviving fee_payments and rebuild the installment status + student balance from scratch. ` +
      `Useful when a payment was deleted but installment row didn't auto-update. ` +
      `Safe — only writes correct values; doesn't delete data.`
    )) return;
    try {
      const res = await api.post(`/installment-payments/recompute/${student.id}`);
      const d = res.data || {};
      alert(
        `Recomputed successfully.\n\n` +
        `Total installment payments tracked: ₹${Number(d.total_installment_payments || 0).toLocaleString('en-IN')}\n` +
        `New balance fee: ₹${Number(d.new_balance_fee || 0).toLocaleString('en-IN')}`
      );
      // Refresh views
      if (selectedStudent) {
        viewInstallments(selectedStudent);
      }
      fetchStudentsWithInstallments();
    } catch (e) {
      alert('Failed to recompute: ' + (e.response?.data?.detail || e.message));
    }
  };

  const getInstallmentStatus = (dueDate, status) => {
    if (status === 'Paid') return { color: 'green', text: 'Paid' };
    if (status === 'Partially Paid') return { color: 'yellow', text: 'Partially Paid' };
    
    const today = new Date();
    const due = new Date(dueDate);
    
    if (due < today) return { color: 'red', text: 'Overdue' };
    if (due.getTime() - today.getTime() < 7 * 24 * 60 * 60 * 1000) return { color: 'yellow', text: 'Due Soon' };
    return { color: 'blue', text: 'Upcoming' };
  };

  const filteredStudents = students.filter(student => {
    // Search filter
    const matchesSearch = student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    // Course filter
    if (filters.course && student.course_name !== filters.course) return false;

    // Session filter
    if (filters.session && student.session !== filters.session) return false;

    // Status filters
    if (filters.overdueOnly && !student.has_overdue) return false;
    if (filters.paidOnly && student.calculated_balance > 0) return false;
    if (filters.unpaidOnly && student.calculated_balance <= 0) return false;

    return true;
  });

  const dashboardStats = calculateDashboardStats();

  // Pagination calculations
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when search/filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const exportToCSV = () => {
    if (!selectedStudent || installments.length === 0) return;

    const csvHeader = 'Installment No,Due Date,Amount,Status,Paid Date,Paid Amount\n';
    const csvData = installments.map(inst => 
      `${inst.installment_number},${inst.due_date},${inst.amount},${inst.status},${inst.paid_date || 'N/A'},${inst.paid_amount || 0}`
    ).join('\n');

    const blob = new Blob([csvHeader + csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedStudent.admission_number}_installments.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Fee Installment Report</h1>
          <p className="text-gray-600 mt-2">View and manage student fee installment plans</p>
        </div>

        {/* Dashboard Statistics */}
        {showDashboard && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-medium">Total Students</p>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{dashboardStats.totalStudents}</p>
                </div>
                <User className="h-12 w-12 text-blue-500 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-medium">Total Due</p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">₹{dashboardStats.totalDue.toLocaleString('en-IN')}</p>
                </div>
                <DollarSign className="h-12 w-12 text-orange-500 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border border-red-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-600 text-sm font-medium">Overdue ({dashboardStats.overdueCount})</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">₹{dashboardStats.totalOverdue.toLocaleString('en-IN')}</p>
                </div>
                <AlertTriangle className="h-12 w-12 text-red-500 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-medium">Collection Rate</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{dashboardStats.collectionRate}%</p>
                </div>
                <TrendingUp className="h-12 w-12 text-green-500 opacity-50" />
              </div>
            </div>
          </div>
        )}

        {/* Search Bar & Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by student name, admission number, or email..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Filter size={18} />
                Filters
                {(filters.course || filters.session || filters.overdueOnly) && (
                  <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold">
                    {[filters.course, filters.session, filters.overdueOnly].filter(Boolean).length}
                  </span>
                )}
              </button>
              {selectedStudents.length > 0 && (
                <button
                  onClick={sendReminders}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Bell size={18} />
                  Send Reminders ({selectedStudents.length})
                </button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-700">
                  <X size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
                  <select
                    value={filters.course}
                    onChange={(e) => handleFilterChange('course', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Courses</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.name}>{course.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
                  <select
                    value={filters.session}
                    onChange={(e) => handleFilterChange('session', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Sessions</option>
                    {filterSessionsByCourse(sessions, courses.find(c => c.name === filters.course)?.id).map(session => (
                      <option key={session.id} value={session.name}>{session.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.overdueOnly}
                        onChange={(e) => handleFilterChange('overdueOnly', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Overdue Only</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear All
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Students List */}
        {!showDetails && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-900">Students List</h2>
              <p className="text-sm text-gray-600 mt-1">Total: {filteredStudents.length} students | Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredStudents.length)}</p>
            </div>

            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : filteredStudents.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No students found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                          onChange={toggleAllStudents}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Course & Session
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Course Fee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Balance Fee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <User className="w-8 h-8 text-gray-400 mr-3" />
                            <div>
                              <div className="font-semibold text-gray-900">{student.full_name}</div>
                              <div className="text-sm text-gray-500">{student.admission_number}</div>
                              <div className="text-sm text-gray-500">{student.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{student.course_name || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{student.session || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-lg font-semibold text-blue-600">
                            ₹{(student.total_course_fee || 0).toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-lg font-semibold text-red-600">
                            ₹{(student.calculated_balance || 0).toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => viewInstallments(student)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <Eye className="w-4 h-4" />
                            View Installments
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {!loading && filteredStudents.length > 0 && totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Show</span>
                  <select
                    data-testid="items-per-page-select"
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>per page</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    data-testid="pagination-prev"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {getPageNumbers().map(page => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`px-3 py-1 border rounded text-sm ${
                        page === currentPage 
                          ? 'bg-blue-600 text-white border-blue-600' 
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    data-testid="pagination-next"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>

                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Installment Details */}
        {showDetails && selectedStudent && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Student Info Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedStudent.full_name}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedStudent.admission_number} | {selectedStudent.email}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => generateInstallmentSettingPDF(selectedStudent, installments)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Printer className="w-4 h-4" />
                    Print Installment Setting
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => fetchRecentPayments(selectedStudent.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    <Trash2 className="w-4 h-4" />
                    Manage Payments
                  </button>
                  {canDeletePayment && (
                    <button
                      onClick={() => recomputeInstallments(selectedStudent)}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      title="Recompute installment status + student balance from actual surviving payments"
                      data-testid="recompute-installments-btn"
                    >
                      🔄 Recompute
                    </button>
                  )}
                  <button
                    onClick={closeDetails}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Back to List
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Payments Modal */}
            {showPayments && (
              <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Recent Payments (You can delete)</h4>
                  <button 
                    onClick={() => setShowPayments(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {recentPayments.length === 0 ? (
                  <p className="text-gray-500">No payments found</p>
                ) : (
                  <div className="space-y-2">
                    {recentPayments.map((payment) => (
                      <div 
                        key={payment.payment_id || payment.id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {payment.receipt_no || payment.payment_id}
                          </p>
                          <p className="text-sm text-gray-600">
                            {payment.payment_for || 'Fee Payment'} | 
                            Amount: ₹{(payment.amount_paid || payment.amount || 0).toLocaleString('en-IN')} |
                            Date: {payment.payment_date || payment.created_at?.split('T')[0]}
                          </p>
                        </div>
                        {canDeletePayment && (
                          <button
                            onClick={() => deletePayment(payment.payment_id || payment.receipt_no || payment.id)}
                            disabled={deletingPayment === (payment.payment_id || payment.receipt_no || payment.id)}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            {deletingPayment === (payment.payment_id || payment.receipt_no || payment.id) ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fee Summary */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total Course Fee</p>
                  <p className="text-lg font-semibold text-gray-900">
                    ₹{(selectedStudent.total_course_fee || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Paid Amount (Booking + Admission + Installments)</p>
                  <p className="text-lg font-semibold text-green-600">
                    ₹{(() => {
                      const bookingAndAdmission = (selectedStudent.seat_booking_amount || 0) + (selectedStudent.admission_fee || 0);
                      const installmentsPaid = installments.reduce((sum, inst) => sum + (inst.paid_amount || 0), 0);
                      return (bookingAndAdmission + installmentsPaid).toLocaleString('en-IN');
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Installments</p>
                  <p className="text-lg font-semibold text-blue-600">{installments.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Balance Fee</p>
                  <p className="text-lg font-semibold text-red-600">
                    ₹{(() => {
                      // ============================================================================
                      // CRITICAL: DO NOT CHANGE THIS CALCULATION FORMULA
                      // ============================================================================
                      // Balance Fee = Course Fee - Admission - Booking - Discount - Scholarship - Installments
                      //
                      // This is the LOCKED formula that must match:
                      // - Backend: /app/backend/routers/students.py
                      // - List view: Lines 34-72 in this same file
                      //
                      // Example: Course=350000, Admission=20000, Booking=40000, Discount=20000, Scholarship=20000, Installments=41667
                      // Balance = 350000 - 20000 - 40000 - 20000 - 20000 - 41667 = 208333
                      //
                      // MUST use parseFloat() to ensure proper numeric conversion
                      // DO NOT ADD any fees - all are ALREADY PAID or DEDUCTIONS
                      // ============================================================================
                      const courseFee = parseFloat(selectedStudent.total_course_fee) || 0;
                      const admissionPaid = parseFloat(selectedStudent.admission_fee) || 0;
                      const bookingPaid = parseFloat(selectedStudent.seat_booking_amount) || 0;
                      const discount = parseFloat(selectedStudent.discount_amount) || 0;
                      const scholarship = parseFloat(selectedStudent.scholarship_amount) || 0;
                      const installmentsPaid = installments.reduce((sum, inst) => sum + (parseFloat(inst.paid_amount) || 0), 0);
                      
                      const balance = courseFee - admissionPaid - bookingPaid - discount - scholarship - installmentsPaid;
                      return balance.toLocaleString('en-IN');
                    })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Installments Table */}
            {installments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No installments set for this student
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Installment #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Late Fine
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Mode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {installments.map((installment) => {
                      const statusInfo = getInstallmentStatus(installment.due_date, installment.status);
                      return (
                        <tr key={installment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="font-semibold text-gray-900">
                                {installment.installment_type === 'semester' ? 'Semester' : 'Installment'} {installment.installment_number}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {formatDate(installment.due_date)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <DollarSign className="w-5 h-5 text-gray-400 mr-1" />
                              <span className="font-semibold text-gray-900">
                                ₹{installment.amount.toLocaleString('en-IN')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                              statusInfo.color === 'red' ? 'bg-red-100 text-red-800' :
                              statusInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {statusInfo.text}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              {(() => {
                                // Calculate fine for overdue installments
                                const today = new Date();
                                const dueDate = new Date(installment.due_date);
                                const isPaid = installment.status === 'Paid';
                                const balance = installment.amount - (installment.paid_amount || 0);
                                
                                if (isPaid || balance <= 0) {
                                  return <span className="text-gray-400">-</span>;
                                }
                                
                                if (today <= dueDate) {
                                  return <span className="text-gray-400">Not Due</span>;
                                }
                                
                                // Calculate days overdue
                                const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                                
                                // Get fine settings
                                const fineEnabled = installment.fine_enabled || installment.fine_setting_id;
                                const gracePeriod = installment.grace_period || 0;
                                const finePerDay = installment.fine_per_day || installment.fine_amount_per_day || 0;
                                
                                if (!fineEnabled || finePerDay <= 0) {
                                  return <span className="text-gray-500">Fine: Disabled</span>;
                                }
                                
                                const fineDays = Math.max(0, daysOverdue - gracePeriod);
                                const fineAmount = fineDays * finePerDay;
                                
                                if (fineAmount > 0) {
                                  return (
                                    <div>
                                      <span className="text-red-600 font-semibold">₹{fineAmount.toLocaleString('en-IN')}</span>
                                      <div className="text-xs text-gray-500">({fineDays} days × ₹{finePerDay})</div>
                                    </div>
                                  );
                                } else {
                                  return <span className="text-yellow-600">Grace Period ({gracePeriod - daysOverdue} days left)</span>;
                                }
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {(installment.status === 'Paid' || installment.status === 'Partially Paid') ? (
                              <div className="text-sm">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                  {installment.payment_mode || 'Cash'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {(installment.status === 'Paid' || installment.status === 'Partially Paid') ? (
                              <div className="text-sm">
                                <div className="text-gray-900">
                                  Paid: ₹{(installment.paid_amount || 0).toLocaleString('en-IN')}
                                  {installment.status === 'Partially Paid' && (
                                    <span className="text-orange-600 font-semibold"> / ₹{installment.amount.toLocaleString('en-IN')}</span>
                                  )}
                                </div>
                                <div className="text-gray-500">Date: {installment.paid_date ? formatDate(installment.paid_date) : 'N/A'}</div>
                                {installment.status === 'Partially Paid' && (
                                  <div className="text-red-600 font-medium mt-1">
                                    Remaining: ₹{(installment.amount - (installment.paid_amount || 0)).toLocaleString('en-IN')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">Not Paid</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="2" className="px-6 py-4 text-right font-semibold text-gray-900">
                        Total Installment Amount:
                      </td>
                      <td className="px-6 py-4 font-bold text-lg text-blue-600">
                        ₹{installments.reduce((sum, inst) => sum + inst.amount, 0).toLocaleString('en-IN')}
                      </td>
                      <td colSpan="4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeInstallmentReport;
